import { useState } from "react";
import PricingDialog from "@/components/PricingDialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Shield, Lock, Search, AlertTriangle, Globe, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SanctionEntity {
  id: string;
  name: string;
  schema: string;
  datasets: string[];
  countries: string[];
  sanctions: string[];
  topics: string[];
  first_seen: string;
  last_seen: string;
  last_change: string;
}

const SANCTIONED_REGIONS = [
  { id: "Russia", label: "🇷🇺 Russia", count: null, severity: "critical" },
  { id: "Iran", label: "🇮🇷 Iran", count: null, severity: "critical" },
  { id: "North Korea", label: "🇰🇵 North Korea", count: null, severity: "critical" },
  { id: "Syria", label: "🇸🇾 Syria", count: null, severity: "high" },
  { id: "Myanmar", label: "🇲🇲 Myanmar", count: null, severity: "high" },
  { id: "Belarus", label: "🇧🇾 Belarus", count: null, severity: "high" },
  { id: "Venezuela", label: "🇻🇪 Venezuela", count: null, severity: "moderate" },
  { id: "Cuba", label: "🇨🇺 Cuba", count: null, severity: "moderate" },
] as const;

// Static curated sanctions overview for each country
const SANCTIONS_OVERVIEW: Record<string, { imposedBy: string[]; types: string[]; sectors: string[]; since: string }> = {
  "Russia": {
    imposedBy: ["US", "EU", "UK", "Canada", "Japan", "Australia"],
    types: ["Asset freeze", "Travel ban", "Trade embargo", "Financial restrictions", "Energy sector"],
    sectors: ["Energy", "Finance", "Defense", "Technology", "Luxury goods"],
    since: "2014 (expanded 2022)",
  },
  "Iran": {
    imposedBy: ["US", "EU", "UK", "UN"],
    types: ["Asset freeze", "Trade embargo", "Nuclear program restrictions", "Oil export ban"],
    sectors: ["Oil & Gas", "Nuclear", "Finance", "Metals", "Automotive"],
    since: "1979 (expanded 2006)",
  },
  "North Korea": {
    imposedBy: ["US", "EU", "UN", "Japan", "South Korea"],
    types: ["Total trade embargo", "Asset freeze", "Travel ban", "Arms embargo"],
    sectors: ["All sectors", "Nuclear", "Missiles", "Coal", "Textiles"],
    since: "2006",
  },
  "Syria": {
    imposedBy: ["US", "EU", "UK", "Arab League"],
    types: ["Asset freeze", "Oil embargo", "Arms embargo", "Travel ban"],
    sectors: ["Oil", "Finance", "Defense", "Government"],
    since: "2011",
  },
  "Myanmar": {
    imposedBy: ["US", "EU", "UK", "Canada"],
    types: ["Asset freeze", "Arms embargo", "Travel ban", "Trade restrictions"],
    sectors: ["Military", "Gems", "Timber", "Finance"],
    since: "2021 (reinstated)",
  },
  "Belarus": {
    imposedBy: ["US", "EU", "UK", "Canada"],
    types: ["Asset freeze", "Trade restrictions", "Aviation ban", "Potash export"],
    sectors: ["Potash", "Oil", "Finance", "Defense"],
    since: "2020 (expanded 2022)",
  },
  "Venezuela": {
    imposedBy: ["US", "EU", "Canada"],
    types: ["Asset freeze", "Oil sector sanctions", "Gold trade ban"],
    sectors: ["Oil & Gas", "Gold", "Finance", "Government"],
    since: "2017",
  },
  "Cuba": {
    imposedBy: ["US"],
    types: ["Trade embargo", "Financial restrictions", "Travel restrictions"],
    sectors: ["All sectors (US embargo)", "Tourism", "Finance"],
    since: "1962",
  },
};

const severityColor = (s: string) => {
  if (s === "critical") return "bg-destructive/20 text-destructive border-destructive/30";
  if (s === "high") return "bg-orange-500/20 text-orange-400 border-orange-500/30";
  return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
};

const SanctionsTracker = () => {
  const [selectedCountry, setSelectedCountry] = useState("Russia");
  const [pricingOpen, setPricingOpen] = useState(false);
  const [isPremium] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["sanctions", selectedCountry],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-sanctions", {
        body: { country: selectedCountry, schema: "LegalEntity" },
      });
      if (error) throw error;
      return data as { results: SanctionEntity[]; total: number; fallback: boolean };
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const overview = SANCTIONS_OVERVIEW[selectedCountry];
  const entities = data?.results || [];
  const freeLimit = 5;
  const visibleEntities = isPremium ? entities : entities.slice(0, freeLimit);

  return (
    <section className="py-8 md:py-12">
      <div className="container mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Sanctions Tracker</h2>
            <p className="text-sm text-muted-foreground">Global sanctions monitoring — OpenSanctions data</p>
          </div>
        </div>

        {/* Country Selector */}
        <div className="flex flex-wrap gap-2">
          {SANCTIONED_REGIONS.map((region) => (
            <Button
              key={region.id}
              variant={selectedCountry === region.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCountry(region.id)}
              className="gap-1.5"
            >
              {region.label}
              <Badge className={`ml-1 text-[10px] px-1.5 py-0 ${severityColor(region.severity)}`}>
                {region.severity}
              </Badge>
            </Button>
          ))}
        </div>

        {/* Overview Card */}
        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-primary/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" /> Imposed By
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1">
                  {overview.imposedBy.map((c) => (
                    <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Sanction Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-xs text-foreground space-y-0.5">
                  {overview.types.slice(0, 4).map((t) => (
                    <li key={t}>• {t}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" /> Sectors Targeted
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1">
                  {overview.sectors.map((s) => (
                    <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Search className="h-3.5 w-3.5" /> Active Since
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-bold text-foreground">{overview.since}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {data?.total ? `${data.total} sanctioned entities found` : "Loading..."}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Sanctioned Entities Table */}
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Sanctioned Entities — {selectedCountry}
              {data?.fallback && (
                <Badge variant="outline" className="ml-2 text-[10px]">Cached data</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Entity</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Datasets</TableHead>
                      <TableHead className="text-xs">Last Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleEntities.map((entity) => (
                      <TableRow key={entity.id}>
                        <TableCell className="text-xs font-medium text-foreground">{entity.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">
                            {entity.schema?.replace("LegalEntity", "Entity").replace("Person", "Individual") || "Entity"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {entity.datasets?.slice(0, 2).join(", ") || "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {entity.last_change ? new Date(entity.last_change).toLocaleDateString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {visibleEntities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-8">
                          No sanctioned entities found for this search.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Premium gate */}
                {!isPremium && entities.length > freeLimit && (
                  <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-card to-transparent flex items-end justify-center pb-4">
                    <Button size="sm" className="gap-1.5" onClick={() => setPricingOpen(true)}>
                      <Lock className="h-3.5 w-3.5" />
                      Unlock all {data?.total || entities.length} entities — Go Premium
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-[10px] text-muted-foreground text-center">
          Data source: OpenSanctions.org — Updated periodically. For informational purposes only.
        </p>
      </div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </section>
  );
};

export default SanctionsTracker;
